import {
  GamoraBase64URLGuard,
  GamoraErrorUnhandledException,
  assert,
  convertEnumToValues,
} from '@ver.id/libs.internal.gamora';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessageGuard } from './base.js';

/**
 * @public
 */
export enum HoruzProtocolChulakMessageAppPairingMethod {
  None = 'None',
  Pin = 'Pin',
}

/**
 * Input type
 * @public
 */
export type HoruzProtocolChulakMessageAppPairingEnterGuardInput = {
  method: HoruzProtocolChulakMessageAppPairingMethod;
  code?: string;
};

/**
 * @public
 */
export class HoruzProtocolChulakMessageAppPairingEnterGuard extends HoruzProtocolChulakMessageGuard<
  HoruzProtocolChulakDataType.STATE_APP_PAIRING_ENTER,
  HoruzProtocolChulakMessageAppPairingEnterGuardInput
> {
  private _method: HoruzProtocolChulakMessageAppPairingMethod =
    HoruzProtocolChulakMessageAppPairingMethod.None;
  private _code = '';

  /**
   * Constructor
   * @param input - the input
   */
  constructor(input: HoruzProtocolChulakMessageAppPairingEnterGuardInput) {
    super();
    this.code = input.code ?? '';
    this.method = input.method;
  }

  /**
   * Gets type
   */
  get type(): HoruzProtocolChulakDataType.STATE_APP_PAIRING_ENTER {
    return HoruzProtocolChulakDataType.STATE_APP_PAIRING_ENTER;
  }

  /**
   * Get
   */
  get method() {
    return this._method;
  }
  set method(method: HoruzProtocolChulakMessageAppPairingMethod) {
    assert(
      convertEnumToValues(HoruzProtocolChulakMessageAppPairingMethod).includes(
        method,
      ),
      'Invalid method: not a valid HoruzProtocolChulakMessageAppPairingMethod.',
    );
    this._method = method;
  }

  /**
   * Get
   */
  get code() {
    return this._code;
  }
  set code(code: string) {
    assert(typeof code === 'string', 'Invalid code: not a valid string.');
    this._code = code;
  }

  /**
   * Gets from JSON
   * @param input - Input string
   */
  static override fromJSON(
    input: unknown,
  ): HoruzProtocolChulakMessageAppPairingEnterGuard {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not an object.',
    );
    return new HoruzProtocolChulakMessageAppPairingEnterGuard(
      input as HoruzProtocolChulakMessageAppPairingEnterGuardInput,
    );
  }

  /**
   * Get from base64URL
   * @param input - The input
   */
  static override fromBase64URL(
    input: unknown,
  ): HoruzProtocolChulakMessageAppPairingEnterGuard {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new HoruzProtocolChulakMessageAppPairingEnterGuard(
      json as HoruzProtocolChulakMessageAppPairingEnterGuardInput,
    );
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON() {
    return {
      code: this.code,
      method: this.method,
    };
  }

  /**
   * To Base64URL
   */
  override toBase64URL(): string {
    const json = this.toJSON();
    return GamoraBase64URLGuard.fromUTF8(JSON.stringify(json)).toBase64URL();
  }

  /**
   * Create clone
   */
  clone() {
    return HoruzProtocolChulakMessageAppPairingEnterGuard.fromJSON(
      this.toJSON(),
    );
  }
}
